import { useNavigate } from 'react-router-dom';
import { C, type WhoopPage } from './whoop-theme';

const SUBPAGES: { label: string; page: WhoopPage }[] = [
  { label: 'Now', page: 'now' },
  { label: 'Plan', page: 'plan' },
  { label: 'Progress', page: 'progress' },
  { label: 'Workout Builder', page: 'builder' },
  { label: 'Trends', page: 'trends' },
  { label: 'Experiments', page: 'experiments' },
  { label: 'Data & Settings', page: 'settings' },
];

const OTHER = [
  { name: 'Strava', mono: 'S', tileBg: '#fc4c02', tileColor: '#fff', connected: true },
  { name: 'Spotify', mono: '♪', tileBg: '#1db954', tileColor: '#04210f', connected: true },
  { name: 'GitHub', mono: 'G', tileBg: '#e8e6e1', tileColor: '#0c0c0e', connected: true },
  { name: 'Audible', mono: 'A', tileBg: '#f8991c', tileColor: '#2a1500', connected: false },
  { name: 'LinkedIn', mono: 'in', tileBg: '#2f6fed', tileColor: '#fff', connected: true },
];

/**
 * The 66px rail that expands to 266px on hover. Expansion is pure CSS
 * (`.ws-row:has(.ws-aside:hover)` in whoop.css) — this component only renders the
 * static structure; the `.ws-*` class names drive the animation.
 */
export function WhoopSidebar({ currentPage }: { currentPage: WhoopPage }) {
  const navigate = useNavigate();

  return (
    <aside
      className="ws-aside"
      style={{
        minWidth: 0,
        borderRight: '1px solid rgba(255,255,255,0.08)',
        background: '#101012',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      <div style={{ padding: '16px 9px', display: 'flex', flexDirection: 'column' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            height: 22,
            padding: '0 4px 0 9px',
            marginBottom: 10,
          }}
        >
          <span
            className="ws-label"
            style={{
              flex: 1,
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: 10,
              letterSpacing: '0.16em',
              color: C.text3,
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
            }}
          >
            Integrations
          </span>
        </div>

        <div
          onClick={() => navigate(`/integrations/whoop/${currentPage}`)}
          className="ws-active ww-whoop-group"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 11,
            padding: '6px 6px 6px 9px',
            borderRadius: 9,
            cursor: 'pointer',
          }}
        >
          <span
            style={{
              width: 30,
              height: 30,
              flex: 'none',
              borderRadius: 8,
              background: 'rgba(181,155,255,0.18)',
              color: C.accent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: 13,
              fontFamily: "'JetBrains Mono',monospace",
              boxShadow: '0 0 0 1.5px rgba(181,155,255,0.55)',
            }}
          >
            W
          </span>
          <span
            className="ws-label"
            style={{ flex: 1, fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap' }}
          >
            WHOOP
          </span>
          <span
            className="ws-chev"
            style={{ fontSize: 11, color: C.text2, width: 14, textAlign: 'center' }}
          >
            ▸
          </span>
        </div>

        <div className="ws-sub">
          <div
            style={{
              margin: '3px 0 3px 24px',
              paddingLeft: 13,
              borderLeft: '1px solid rgba(255,255,255,0.08)',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            {SUBPAGES.map((item) => {
              const active = item.page === currentPage;
              return (
                <a
                  key={item.page}
                  href={`#/integrations/whoop/${item.page}`}
                  onClick={(event) => {
                    event.preventDefault();
                    navigate(`/integrations/whoop/${item.page}`);
                  }}
                  className="ww-sub-link"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 9,
                    padding: '7px 9px',
                    borderRadius: 7,
                    cursor: 'pointer',
                    fontSize: '13.5px',
                    fontWeight: active ? 700 : 500,
                    color: active ? C.text : C.text2,
                    background: active ? 'rgba(181,155,255,0.13)' : 'transparent',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <span
                    style={{
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      background: active ? C.accent : 'transparent',
                      flex: 'none',
                    }}
                  />
                  {item.label}
                </a>
              );
            })}
          </div>
        </div>

        <div className="ws-divider ws-label" />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {OTHER.map((item) => (
            <div
              key={item.name}
              onClick={() => navigate('/integrations')}
              className="ww-other-row"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 11,
                padding: '6px 6px 6px 9px',
                borderRadius: 9,
                cursor: 'pointer',
              }}
            >
              <span
                style={{
                  width: 30,
                  height: 30,
                  flex: 'none',
                  borderRadius: 8,
                  background: item.tileBg,
                  color: item.tileColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: 12,
                  fontFamily: "'JetBrains Mono',monospace",
                }}
              >
                {item.mono}
              </span>
              <span
                className="ws-label"
                style={{
                  flex: 1,
                  fontSize: 13,
                  fontWeight: 500,
                  color: '#cbc9c2',
                  whiteSpace: 'nowrap',
                }}
              >
                {item.name}
              </span>
              <span
                className="ws-label"
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: '50%',
                  background: item.connected ? C.good : C.text3,
                  flex: 'none',
                }}
              />
            </div>
          ))}
        </div>
      </div>
    </aside>
  );
}
